import { useLanguage } from '../contexts/LanguageContext';

/**
 * Enhanced translation hook with additional utilities
 * 
 * @returns {Object} Translation utilities and functions
 */
export const useTranslation = () => {
  const {
    currentLanguage,
    changeLanguage,
    getCurrentLanguageInfo,
    getAvailableLanguages,
    t,
    isRTL
  } = useLanguage();

  /**
   * Get translation with interpolation support
   * 
   * @param {string} key - Translation key (e.g., 'common.save')
   * @param {Object} values - Values to interpolate (e.g., {name: 'John'})
   * @param {string} defaultValue - Default value if translation not found
   * @returns {string} Translated and interpolated string
   */
  const translate = (key, values = {}, defaultValue = key) => {
    let translation = t(key, defaultValue);

    // Simple interpolation: replace {{variable}} with values
    if (values && typeof values === 'object') {
      Object.keys(values).forEach(variable => {
        const regex = new RegExp(`{{\\s*${variable}\\s*}}`, 'g');
        translation = translation.replace(regex, values[variable]);
      });
    }

    return translation;
  };

  /**
   * Get plural translation based on count
   * 
   * @param {string} key - Translation key
   * @param {number} count - Number for pluralization
   * @param {Object} values - Values to interpolate
   * @returns {string} Pluralized translation
   */
  const translatePlural = (key, count, values = {}) => {
    const pluralKey = count === 1 ? key : `${key}_plural`;
    const translation = t(pluralKey, t(key));
    
    return translate(translation, { ...values, count }, translation);
  };

  /**
   * Get translation for menu items
   * 
   * @param {string} menuKey - Menu key (e.g., 'dashboard', 'users')
   * @returns {string} Menu translation
   */
  const getMenuLabel = (menuKey) => {
    return t(`menu.${menuKey}`, menuKey);
  };

  /**
   * Get translation for common actions
   * 
   * @param {string} actionKey - Action key (e.g., 'save', 'cancel', 'delete')
   * @returns {string} Action translation
   */
  const getCommonLabel = (actionKey) => {
    return t(`common.${actionKey}`, actionKey);
  };

  /**
   * Get translation for messages (legacy - for backward compatibility)
   * 
   * @param {string} messageKey - Message key (e.g., 'login_success', 'error_occurred')
   * @returns {string} Message translation
   */
  const getMessage = (messageKey) => {
    // Handle both prefixed and non-prefixed keys
    if (messageKey.startsWith('messages.')) {
      return t(messageKey, messageKey);
    }
    
    // Try the key as-is first (for keys like 'app.title', 'login.title')
    const directTranslation = t(messageKey, null);
    if (directTranslation !== null && directTranslation !== messageKey) {
      return directTranslation;
    }
    
    // Fallback to messages prefix for backward compatibility
    return t(`messages.${messageKey}`, messageKey);
  };

  /**
   * Get translation for form labels in a specific module
   * 
   * @param {string} module - Module name (e.g., 'users', 'vehicles')
   * @param {string} labelKey - Label key within the module
   * @returns {string} Label translation
   */
  const getModuleLabel = (module, labelKey) => {
    return t(`${module}.${labelKey}`, labelKey);
  };

  /**
   * Format number according to current language locale
   * 
   * @param {number} number - Number to format
   * @param {Object} options - Intl.NumberFormat options
   * @returns {string} Formatted number
   */
  const formatNumber = (number, options = {}) => {
    const locale = getLanguageLocale();
    return new Intl.NumberFormat(locale, options).format(number);
  };

  /**
   * Format currency according to current language locale
   * 
   * @param {number} amount - Amount to format
   * @param {string} currency - Currency code (default: LAK)
   * @returns {string} Formatted currency
   */
  const formatCurrency = (amount, currency = 'LAK') => {
    const locale = getLanguageLocale();
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0
    }).format(amount);
  };

  /**
   * Format date according to current language locale
   * 
   * @param {Date|string} date - Date to format
   * @param {Object} options - Intl.DateTimeFormat options
   * @returns {string} Formatted date
   */
  const formatDate = (date, options = {}) => {
    const locale = getLanguageLocale();
    const dateObj = date instanceof Date ? date : new Date(date);
    
    const defaultOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      ...options
    };

    return new Intl.DateTimeFormat(locale, defaultOptions).format(dateObj);
  };

  /**
   * Format time according to current language locale
   * 
   * @param {Date|string} time - Time to format
   * @param {Object} options - Intl.DateTimeFormat options
   * @returns {string} Formatted time
   */
  const formatTime = (time, options = {}) => {
    const locale = getLanguageLocale();
    const timeObj = time instanceof Date ? time : new Date(time);
    
    const defaultOptions = {
      hour: '2-digit',
      minute: '2-digit',
      ...options
    };

    return new Intl.DateTimeFormat(locale, defaultOptions).format(timeObj);
  };

  /**
   * Get locale string for Intl formatting
   * 
   * @returns {string} Locale string
   */
  const getLanguageLocale = () => {
    const localeMap = {
      en: 'en-US',
      th: 'th-TH',
      la: 'lo-LA' // Lao locale
    };
    return localeMap[currentLanguage] || 'en-US';
  };

  /**
   * Check if current language is Thai
   * 
   * @returns {boolean} True if current language is Thai
   */
  const isThai = () => currentLanguage === 'th';

  /**
   * Check if current language is Lao
   * 
   * @returns {boolean} True if current language is Lao
   */
  const isLao = () => currentLanguage === 'la';

  /**
   * Check if current language is English
   * 
   * @returns {boolean} True if current language is English
   */
  const isEnglish = () => currentLanguage === 'en';

  /**
   * Get direction CSS class based on language
   * 
   * @returns {string} CSS direction class
   */
  const getDirectionClass = () => {
    return isRTL() ? 'rtl' : 'ltr';
  };

  return {
    // Core translation functions
    t,
    translate,
    translatePlural,
    
    // Specific translation getters
    getMenuLabel,
    getCommonLabel,
    getMessage,
    getModuleLabel,
    
    // Formatting functions
    formatNumber,
    formatCurrency,
    formatDate,
    formatTime,
    getLanguageLocale,
    
    // Language information
    currentLanguage,
    changeLanguage,
    getCurrentLanguageInfo,
    getAvailableLanguages,
    
    // Language checks
    isThai,
    isLao,
    isEnglish,
    isRTL,
    getDirectionClass,
  };
};

export default useTranslation;